/**
 * MatchHeight.js
 * A utility to match the heights of elements dynamically.
 * 
 */

(function (global) {
  class MatchHeight {
    constructor(selector, options = {}) {
      // Default options
      this.defaultOptions = {
        parent: null,   // Parent container to limit the scope
        byRow: true,    // Group elements by rows
        timeOut: 50,    // Timeout to debounce resizing logic
      };

      // Merge user options with defaults
      this.options = { ...this.defaultOptions, ...options };

      // Selector for elements to match
      this.selector = selector;

      // Initialize elements cache
      this.elements = [];

      // Check if target elements exist before initializing
      if (this.selector && document.querySelector(this.selector)) {
        // Initialize the plugin once the DOM is ready
        document.readyState !== "loading"
          ? this.init()
          : document.addEventListener("DOMContentLoaded", () => this.init());

        // Update heights on window resize
        window.addEventListener(
          "resize",
          this.debounce(() => this.update(), this.options.timeOut)
        );
      } else {
        console.warn(`⛔ No elements found for selector: "${this.selector}". MatchHeight not initialized.`);
      }
    }

    /**
     * Initialize the plugin by finding and setting heights.
     */
    init() {
      this.reset(); // Find and reset groups
      this.setHeights(); // Apply initial heights
    }

    /**
     * Find elements to apply MatchHeight and organize them.
     */
    findElements() {
      let elementsToMatch;

      if (this.options.parent && document.querySelectorAll(this.options.parent).length) {
        // Find elements within the parent
        document.querySelectorAll(this.options.parent).forEach((parent) => {
          elementsToMatch = parent.querySelectorAll(this.selector);
          this.createGroup(elementsToMatch, parent);
        });
      } else {
        // Find elements globally
        elementsToMatch = document.querySelectorAll(this.selector);
        this.createGroup(elementsToMatch);
      }

      // Group elements into rows if required
      if (this.options.byRow) {
        this.groupByRows();
      }
    }

    /**
     * Group elements by their vertical offset (rows).
     */
    groupByRows() {
      this.elements.forEach((group) => {
        const rowGroups = [];

        group.elements.forEach((el) => {
          const offsetTop = el.getBoundingClientRect().top;

          let row = rowGroups.find((r) => Math.abs(r.offsetTop - offsetTop) < 1);
          if (!row) {
            row = { offsetTop, elements: [] };
            rowGroups.push(row);
          }

          row.elements.push(el);
        });

        group.rows = rowGroups;
      });
    }

    /**
     * Apply the tallest height to each group of elements.
     */
    setHeights() {
      if (!this.elements.length) {
        console.error("No elements found for MatchHeight.");
        return;
      }

      this.elements.forEach((group) => {
        const rows = group.rows.length ? group.rows : [{ elements: group.elements }];

        rows.forEach((row) => {
          const heights = row.elements.map((el) => {
            el.style.minHeight = ""; // Reset height first
            return el.offsetHeight;
          });

          const maxHeight = Math.max(...heights);

          row.elements.forEach((el) => {
            el.style.minHeight = `${maxHeight}px`; // Apply max height
          });
        });
      });
    }

    /**
     * Group elements with a parent container for caching.
     * @param {NodeList} elements - Elements to group
     * @param {Element} parent - Parent container
     */
    createGroup(elements, parent = null) {
      this.elements.push({
        elements: Array.from(elements), // Convert NodeList to Array
        rows: [],
        parent,
      });
    }

    /**
     * Update heights dynamically (e.g., on resize).
     */
    update() {
      this.reset(); // Recalculate the groups
      this.setHeights(); // Reapply heights
    }

    /**
     * Reset and recalculate groups and heights.
     */
    reset() {
      this.elements = [];
      this.findElements();
    }

    /**
     * Utility: Debounce a function to delay execution.
     * @param {Function} callback - Function to debounce
     * @param {number} delay - Delay in milliseconds
     */
    debounce(callback, delay) {
      let timer;
      return () => {
        clearTimeout(timer);
        timer = setTimeout(callback, delay);
      };
    }
  }

  // Expose MatchHeight globally
  global.MatchHeight = MatchHeight;
})(window);